import { useEffect, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { useCacheTable, useForm, useSearch } from '../ui/hooks.js'
import { OrtsFeldPaar, StrassenFeld, useOrtschaften, useStrassen } from '../ui/OrtsFelder.js'
import { PROJECT_SEARCH } from '../net/search.js'
import {
  createProject,
  removeProject,
  setProjectMembers,
  updateProject,
  type ProjectFields
} from '../net/records.js'
import { db, type CacheRow } from '../net/localDb.js'
import { api, type TenantUser } from '../net/apiClient.js'

export function Projects({ session }: { session: SessionUser }): JSX.Element {
  const { rows, loading } = useCacheTable<CacheRow>('projects')
  const { rows: customers } = useCacheTable<CacheRow>('customers')
  const { query, setQuery, results, tookMs } = useSearch(rows, PROJECT_SEARCH)
  const [editing, setEditing] = useState<CacheRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const isSuperuser = session.role === 'superuser'

  // Archivierte Projekte gehören nicht in die Liste, mit der jemand ein neues
  // Projekt sucht. Bestehende Rapporte zeigen sie weiterhin.
  const visible = showArchived ? results : results.filter((p) => p.status !== 'archiviert')
  const archivedCount = rows.filter((p) => p.status === 'archiviert').length

  const customerName = (id: unknown): string =>
    String(customers.find((c) => c.id === id)?.name ?? '—')

  return (
    <>
      <div className="card">
        <div className="listhead">
          <h2>Projekte</h2>
          <span className="muted">{rows.length - archivedCount}</span>
        </div>
        {!isSuperuser && (
          <p className="muted small">Du siehst nur Projekte, die dir zugewiesen sind.</p>
        )}
        <input
          autoComplete="off"
          id="projekt-suche"
          type="search"
          placeholder="Projekt, Ort, Strasse …"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <p className="muted small">
            {visible.length} Treffer · lokal gesucht in {tookMs} ms
          </p>
        )}
        {archivedCount > 0 && (
          <label className="inline">
            <input
              autoComplete="off"
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Archivierte anzeigen ({archivedCount})
          </label>
        )}

        {loading ? (
          <p className="muted">Wird geladen …</p>
        ) : visible.length === 0 ? (
          <p className="muted">
            {query ? `Kein Projekt mit „${query}“.` : 'Noch keine Projekte erfasst.'}
          </p>
        ) : (
          <ul className="records">
            {visible.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => isSuperuser && setEditing(p)}>
                  <span className="rec-name">
                    {String(p.name)}
                    {p.pending === true && <span className="pill small">wartet</span>}
                    {p.status === 'abgeschlossen' && (
                      <span className="pill small">abgeschlossen</span>
                    )}
                    {p.status === 'archiviert' && <span className="pill small">archiviert</span>}
                  </span>
                  <span className="rec-sub">
                    {customerName(p.customerId)} ·{' '}
                    {[p.street, [p.zip, p.city].filter(Boolean).join(' ')]
                      .filter((x) => x && String(x).trim())
                      .join(', ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {isSuperuser && (
          <ActionButton
            variant="secondary"
            labels={{ idle: 'Neues Projekt', busy: 'Öffnet …' }}
            onAction={async () => setCreating(true)}
          />
        )}
      </div>

      {isSuperuser && (creating || editing) && (
        <ProjectForm
          session={session}
          record={editing}
          customers={customers}
          onDone={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

function ProjectForm({
  session,
  record,
  customers,
  onDone
}: {
  session: SessionUser
  record: CacheRow | null
  customers: CacheRow[]
  onDone: () => void
}): JSX.Element {
  const { values, set } = useForm<ProjectFields>(
    record
      ? {
          customerId: String(record.customerId ?? ''),
          name: String(record.name ?? ''),
          street: (record.street as string) ?? '',
          zip: (record.zip as string) ?? '',
          city: (record.city as string) ?? '',
          notes: (record.notes as string) ?? '',
          status: (record.status as 'offen' | 'abgeschlossen') ?? 'offen'
        }
      : { customerId: customers[0] ? String(customers[0].id) : '', name: '', status: 'offen' }
  )
  const ortschaften = useOrtschaften()
  const strassen = useStrassen()
  const [users, setUsers] = useState<TenantUser[]>([])
  const [assigned, setAssigned] = useState<string[]>([])
  const actor = { tenantId: session.tenantId, userId: session.userId }

  useEffect(() => {
    // Die Benutzerliste ist klein und ändert selten – sie liegt nicht im Cache,
    // weil ein Worker sie ohnehin nie sehen darf.
    void api
      .listUsers()
      .then((r) => setUsers(r.users))
      .catch(() => setUsers([]))
  }, [])

  useEffect(() => {
    if (!record) return
    void db.projectMembers
      .where('projectId')
      .equals(String(record.id))
      .toArray()
      .then((rows) => setAssigned(rows.map((r) => r.userId)))
  }, [record])

  /** Adresse vom Kunden übernehmen – als Vorschlag, überschreibbar. */
  function takeCustomerAddress(customerId: string): void {
    const c = customers.find((x) => String(x.id) === customerId)
    if (!c) return
    if (!values.street) set('street', (c.street as string) ?? '')
    if (!values.zip) set('zip', (c.zip as string) ?? '')
    if (!values.city) set('city', (c.city as string) ?? '')
  }

  return (
    <div className="card">
      <h2>{record ? 'Projekt bearbeiten' : 'Neues Projekt'}</h2>

      <label htmlFor="p-customer">Kunde</label>
      <select
        id="p-customer"
        value={values.customerId}
        onChange={(e) => {
          set('customerId', e.target.value)
          takeCustomerAddress(e.target.value)
        }}
      >
        <option value="">— bitte wählen —</option>
        {customers.map((c) => (
          <option key={String(c.id)} value={String(c.id)}>
            {String(c.name)}
          </option>
        ))}
      </select>

      <label htmlFor="p-name">Bezeichnung</label>
      <input
        autoComplete="off"
        id="p-name"
        value={values.name}
        onChange={(e) => set('name', e.target.value)}
        placeholder="z.B. Wohnung 3. OG"
      />

      <label htmlFor="p-street">Strasse</label>
      <StrassenFeld
        id="p-street"
        wert={values.street ?? ''}
        aufWert={(w) => set('street', w)}
        aufAdresse={(a) => {
          set('street', a.strasse)
          if (a.plz) set('zip', a.plz)
          if (a.ort) set('city', a.ort)
        }}
        bestand={strassen}
      />
      <OrtsFeldPaar
        praefix="p"
        ids={{ plz: 'p-zip', ort: 'p-city' }}
        plz={values.zip ?? ''}
        ort={values.city ?? ''}
        aufAenderung={({ plz, ort }) => {
          set('zip', plz)
          set('city', ort)
        }}
        ortschaften={ortschaften}
        klassen={{ reihe: 'row', feld: '', plz: 'narrow', ort: 'grow' }}
      />
      <p className="muted small">
        Die Adresse wird beim Wählen des Kunden vorgeschlagen und lässt sich überschreiben.
      </p>

      <label htmlFor="p-status">Status</label>
      <select
        id="p-status"
        value={values.status ?? 'offen'}
        onChange={(e) => set('status', e.target.value as 'offen' | 'abgeschlossen')}
      >
        <option value="offen">offen</option>
        <option value="abgeschlossen">abgeschlossen</option>
      </select>
      <p className="muted small">
        Abgeschlossen heisst fertig gearbeitet. Aus den Auswahllisten verschwindet ein Projekt erst,
        wenn es archiviert wird.
      </p>

      <label htmlFor="p-notes">Notizen</label>
      <textarea
        id="p-notes"
        rows={2}
        value={values.notes ?? ''}
        onChange={(e) => set('notes', e.target.value)}
      />

      {record && (
        <>
          <label>Zugewiesene Personen</label>
          <p className="muted small">
            Nur zugewiesene Personen sehen dieses Projekt – geprüft wird das auf dem Server, nicht
            in der Oberfläche.
          </p>
          <ul className="checks">
            {users
              .filter((u) => u.role === 'worker')
              .map((u) => (
                <li key={u.userId}>
                  <label>
                    <input
                      autoComplete="off"
                      type="checkbox"
                      checked={assigned.includes(u.userId)}
                      onChange={(e) =>
                        setAssigned((prev) =>
                          e.target.checked
                            ? [...prev, u.userId]
                            : prev.filter((x) => x !== u.userId)
                        )
                      }
                    />
                    {u.name}
                  </label>
                </li>
              ))}
            {users.filter((u) => u.role === 'worker').length === 0 && (
              <li className="muted small">Keine Mitarbeiter im Betrieb.</li>
            )}
          </ul>
        </>
      )}

      <div className="actions-row">
        <ActionButton
          id="btn-projekt-speichern"
          labels={{ idle: 'Speichern', busy: 'Speichert …', success: 'Gespeichert' }}
          onAction={async () => {
            if (!values.customerId) {
              notify.warn('Bitte einen Kunden wählen.')
              return
            }
            if (!values.name.trim()) {
              notify.warn('Ohne Bezeichnung geht es nicht.')
              return
            }
            if (record) {
              await updateProject(actor, String(record.id), values, Number(record.version ?? 1))
              await setProjectMembers(actor, String(record.id), assigned)
            } else {
              await createProject(actor, values)
            }
            onDone()
          }}
          onError={(err) => notifyError(err)}
        />
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Abbrechen', busy: '…' }}
          onAction={async () => onDone()}
        />
        {record && (
          <ActionButton
            variant="quiet"
            actionClass="B"
            className="danger"
            labels={{
              idle: 'Löschen',
              busy: 'Prüft Verwendung …',
              explain: 'Suche Rapporte und Belege …',
              success: 'Erledigt'
            }}
            onAction={async () => {
              const r = await removeProject(actor, String(record.id))
              if (r.disposal === 'geloescht') notify.ok(r.message)
              else notify.warn(r.message)
              onDone()
            }}
            onError={(err) => notifyError(err)}
          />
        )}
      </div>
      {record && (
        <p className="muted small">
          Wurde am Projekt schon rapportiert oder ein Beleg erstellt, wird es archiviert statt
          gelöscht. Die Rapporte bleiben erhalten und bleiben dem Projekt zugeordnet.
        </p>
      )}
    </div>
  )
}
